import DesignButton from 'components/button/DesignButton';
import InfoBox from 'components/infoBox/InfoBox';
import { AlertIcon, BackIcon } from 'components/svgs';
import WithLoadingSpinner from 'components/withLoadingSpinner/WithLoadingSpinner';
import { useCapacityPlanRunQuery } from 'core/graphql/CapacityPlanning/CapacityPlanRun.generated';
import { useCapacityPlanRunsQuery } from 'core/graphql/CapacityPlanning/CapacityPlanRuns.generated';
import { CapacityPlanRunStatus } from 'core/graphql/types';
import useAgenciesDataStore from 'core/hooks/useAgenciesDataStore';
import useAgencySettingsDataStore from 'core/hooks/useAgencySettingsDataStore';
import useAppDispatch from 'core/hooks/useAppDispatch';
import usePermissions from 'core/hooks/usePermissions';
import usePolledQuery from 'core/hooks/usePolledQuery';
import { setSettingsDraft } from 'core/store/CapacityPlanningSlice';
import { useEffect, useRef } from 'react';
import { withAuthenticationRequired } from 'react-oidc-context';
import CapacityPlanTabs, {
  capacityPlanTabId,
  capacityPlanTabPanelId,
} from './CapacityPlanTabs';
import CapacityPlanToolbar, { AgencyOption } from './CapacityPlanToolbar';
import DeliveriesTab from './DeliveriesTab';
import NewRun from './NewRun';
import ResultTab from './ResultTab';
import { isRunSolving } from './runProgress';
import RunHeader from './RunHeader';
import { prefillDraft, runToDraft } from './RunSettings';
import RunsList from './RunsList';
import SettingsTab from './SettingsTab';
import useCapacityPlanUrl from './useCapacityPlanUrl';

/**
 * Capacity Planning page shell: pick an agency in the toolbar, then either
 * browse its runs, create one, or work on the open run's tabs. The URL owns
 * the navigation, so every state here is a shareable link.
 */
const CapacityPlanning = () => {
  const dispatch = useAppDispatch();
  const { hasPermission, isHeadquarters } = usePermissions();
  const canEdit = hasPermission('capacityPlanning.edit');
  const {
    agencySelection,
    agencyId,
    isNetworkRun,
    runId,
    tab,
    isNewRun,
    selectAgency,
    openRun,
    setTab,
    openNewRun,
    backToRuns,
  } = useCapacityPlanUrl();

  const { agenciesData, areAgenciesLoading } = useAgenciesDataStore();
  // Agency.id (this page's `agencyId` URL param) is the Agency's own Guid,
  // which is also what the capacity planning API expects. A Network Run has no
  // Agency, so it has no synced Depot either.
  const { agencySettings, loadingSettings } = useAgencySettingsDataStore(
    agencyId ?? '',
  );

  // A null agencyId asks for the country's Network Runs, which only a
  // headquarters user may read.
  const {
    data: runsData,
    loading: areRunsLoading,
    error: runsError,
    refetch: refetchRuns,
  } = useCapacityPlanRunsQuery({
    skip: agencySelection === null,
    variables: { agencyId },
    // The client's default policy ignores GraphQL errors, which would leave this
    // page unable to tell a refused query from an empty answer.
    errorPolicy: 'all',
  });
  // Newest Run first, so the selector and the settings prefill agree.
  const runs = [...(runsData?.capacityPlanRuns ?? [])].sort((left, right) =>
    right.createdAt.localeCompare(left.createdAt),
  );
  const newestRun = runs.length > 0 ? runs[0] : undefined;

  // `?New=1` wins over a leftover `?Run=`: the form is being filled in, so the
  // Run behind it is neither queried nor allowed to overwrite the draft.
  const {
    data: runData,
    loading: isRunLoading,
    error: runError,
    refetch: refetchRun,
  } = useCapacityPlanRunQuery({
    skip: runId === null || isNewRun,
    variables: { id: runId ?? '' },
    errorPolicy: 'all',
  });
  const run = runData?.capacityPlanRun ?? undefined;

  // A Queued or Computing Run finishes on a worker, with nothing pushed back to
  // the browser, so the only way to see it move is to ask again. Polling belongs
  // to the page and not to the Result tab: the run header shows the same progress
  // on every tab, and it must not freeze because the supervisor stepped into
  // Deliveries while the plan is being computed.
  usePolledQuery({
    fetchFn: refetchRun,
    shouldPoll: run !== undefined && isRunSolving(run.status),
  });

  // The open Run's own settings and its Label are what the Settings tab edits:
  // leaving the Label out opens that tab with an empty box and a change nobody made.
  useEffect(() => {
    if (run && !isNewRun) {
      dispatch(setSettingsDraft(runToDraft(run.settings, run.label)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.id, isNewRun, dispatch]);

  // A Run that does not exist yet starts from the agency's newest Run, else
  // from the nominal defaults with the Depot synced from LM. Prefilling happens
  // once per opening of the form, and only once both sources have settled: a
  // late answer must never overwrite what the supervisor has already typed.
  const isPrefillReady = !areRunsLoading && !loadingSettings;
  const hasPrefilled = useRef(false);
  useEffect(() => {
    if (!isNewRun) {
      hasPrefilled.current = false;
      return;
    }
    if (!isPrefillReady || hasPrefilled.current) {
      return;
    }
    hasPrefilled.current = true;
    dispatch(
      setSettingsDraft(prefillDraft(newestRun?.settings, agencySettings)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewRun, isPrefillReady, newestRun?.id, agencySettings?.id, dispatch]);

  const agencies: AgencyOption[] = agenciesData.map((agency) => ({
    value: agency.id as string,
    label: agency.name ?? agency.id,
  }));
  const agencyName = isNetworkRun
    ? 'Network'
    : (agencies.find((option) => option.value === agencyId)?.label ?? '');

  const handleRunCreated = async (createdRunId: string) => {
    // The upload is a REST call, so Apollo's cached run list does not know the
    // new Run yet: refetch first, otherwise the run selector has no option to
    // show for the Run the tabs are already displaying.
    await refetchRuns();
    openRun(createdRunId);
  };

  // A Run id from a stale or hand-edited link belongs to another agency, or to
  // no Run at all; the run list is the authority once it has loaded. A failed
  // run list knows nothing, so it may not declare the Run unknown either.
  const isUnknownRun =
    runId !== null &&
    !areRunsLoading &&
    runsData !== undefined &&
    !runs.some((candidate) => candidate.id === runId);

  // One page-level spinner covers the run query too, so a deep link that loads
  // the agencies and the Run at once never stacks two overlays. The agency
  // settings only prefill the New run form, which waits for them on its own, so
  // they must not hold the whole page back. The overlay belongs to a page with
  // nothing on it yet: the client revalidates against the network on every poll
  // of a solving Run, and covering the page every few seconds would make the
  // progress it reports unwatchable.
  const isPageLoading =
    (areAgenciesLoading && agenciesData.length === 0) ||
    (runId !== null && isRunLoading && run === undefined);

  // A Run that failed before it was ever planned failed on its file: the
  // message is the parse error, and no tab can show anything useful yet.
  const parseError =
    run &&
    run.status === CapacityPlanRunStatus.Failed &&
    run.solveProgress.total === 0
      ? run.error
      : null;

  const runView = run ? (
    <div className="flex flex-col gap-6">
      <RunHeader run={run} canEdit={canEdit} />
      {parseError && (
        <InfoBox
          variant="danger"
          icon={<AlertIcon />}
          title="The file could not be read"
        >
          {parseError}
        </InfoBox>
      )}
      <CapacityPlanTabs
        activeTab={tab}
        onTabClick={setTab}
        deliveriesCount={run.totalDeliveries}
      />
      <div
        id={capacityPlanTabPanelId}
        role="tabpanel"
        aria-labelledby={capacityPlanTabId(tab)}
      >
        {tab === 'deliveries' && <DeliveriesTab run={run} canEdit={canEdit} />}
        {tab === 'result' && <ResultTab run={run} />}
        {tab === 'settings' && <SettingsTab run={run} canEdit={canEdit} />}
      </div>
    </div>
  ) : null;

  const backToRunsButton = (
    <DesignButton variant="secondary" onClickHandler={backToRuns}>
      <BackIcon />
      Back to runs
    </DesignButton>
  );

  const content = () => {
    if (agencySelection === null) {
      return (
        <p className="text-sm">Select an agency to plan its deliveries.</p>
      );
    }
    if (isNewRun) {
      return (
        <NewRun
          agencyId={agencyId}
          agencyName={agencyName}
          isNetworkRun={isNetworkRun}
          canEdit={canEdit}
          onCreated={handleRunCreated}
          onCancel={backToRuns}
        />
      );
    }
    if (runId !== null) {
      if (runError) {
        return (
          <InfoBox
            variant="danger"
            icon={<AlertIcon />}
            action={backToRunsButton}
          >
            This run could not be loaded. {runError.message}
          </InfoBox>
        );
      }
      if (isUnknownRun) {
        return (
          <InfoBox
            variant="warning"
            icon={<AlertIcon />}
            action={backToRunsButton}
          >
            This run does not exist or belongs to another agency
          </InfoBox>
        );
      }
      return runView;
    }
    if (runsError) {
      return (
        <InfoBox variant="danger" icon={<AlertIcon />}>
          The runs could not be loaded. {runsError.message}
        </InfoBox>
      );
    }
    return (
      <RunsList runs={runs} isLoading={areRunsLoading} onOpenRun={openRun} />
    );
  };

  return (
    <WithLoadingSpinner isLoading={isPageLoading}>
      <div className="min-h-full dark:bg-design-black dark:text-white">
        <div className="mx-auto flex max-w-[1376px] flex-col gap-6 px-8 pb-12 pt-8">
          <h2 className="text-2xl font-bold">Capacity planning</h2>
          <CapacityPlanToolbar
            agencies={agencies}
            agencySelection={agencySelection}
            areAgenciesLoading={areAgenciesLoading}
            onAgencyChange={selectAgency}
            isHeadquarters={isHeadquarters}
            runs={runs}
            selectedRunId={isNewRun ? null : runId}
            areRunsLoading={areRunsLoading}
            onRunChange={(nextRunId) =>
              nextRunId ? openRun(nextRunId) : backToRuns()
            }
            canEdit={canEdit}
            onNewRun={openNewRun}
          />
          {content()}
        </div>
      </div>
    </WithLoadingSpinner>
  );
};

export default withAuthenticationRequired(CapacityPlanning);
